import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useSWR from 'swr';
import { Box, Flex, Text, Heading, Button } from 'rebass';
import { useWindowSize } from 'react-use';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import { getSWR } from 'utils/api';
import Loading from 'app/loading';
import SliderPageRenderer from 'collections/book-creation/components/SliderComponents/SliderPageRenderer';
import { transformAllPages } from 'collections/book-creation/helper';
import { bookPagesKey, BOOK_PAGES_STATES } from './api';
import { FT_COLORS } from './theme';

const POLLING_STATES = new Set([null, undefined, BOOK_PAGES_STATES.CREATING]);

const getGenerationState = (data) =>
  data?.data?.meta?.state_for_page_generation || data?.data?.meta?.state || null;

// One book page rendered with ALL its layers, scaled to fit the column. Reuses the
// book-module page renderer; layer content resolves from the entities the SWR fetch
// populated. `index` parity drives the left/right page rounding (book spread look).
const PageCard = ({ page, index, targetWidth }) => {
  const attributes = page.attributes || {};
  const nativeWidth = Number(attributes.width) || targetWidth;
  const nativeHeight = Number(attributes.height) || targetWidth * 1.3;
  const zoom = (targetWidth / nativeWidth) * 100;

  return (
    <Box
      sx={{
        width: `${targetWidth}px`,
        height: `${nativeHeight * (zoom / 100)}px`,
        overflow: 'hidden',
        bg: attributes['background-colour'] || '#fff',
      }}
    >
      <SliderPageRenderer
        attributes={attributes}
        layers={page.layers}
        zoom={zoom}
        index={index}
        isReadOnly
        isFromTFT
      />
    </Box>
  );
};

PageCard.propTypes = {
  page: PropTypes.shape({}).isRequired,
  index: PropTypes.number.isRequired,
  targetWidth: PropTypes.number.isRequired,
};

// Blank right-hand page for an odd final spread (lined placeholder like the flipbook).
const BlankPage = ({ w, h }) => (
  <Box
    sx={{
      width: `${w}px`,
      height: `${h}px`,
      bg: '#f5efe0',
      backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 27px, rgba(0,0,0,0.05) 28px)',
    }}
  />
);
BlankPage.propTypes = { w: PropTypes.number.isRequired, h: PropTypes.number.isRequired };

const Arrow = ({ dir, disabled, onClick }) => (
  <Box
    as="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      flex: '0 0 auto',
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      bg: '#fff',
      boxShadow: 'low',
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      color: FT_COLORS.primary,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {dir === 'left' ? <FiChevronLeft size={20} /> : <FiChevronRight size={20} />}
  </Box>
);
Arrow.propTypes = {
  dir: PropTypes.oneOf(['left', 'right']).isRequired,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};
Arrow.defaultProps = { disabled: false };

// "Page Preview" tab — polls book_pages until finished, then shows the generated
// pages as two-up book spreads with sliding prev/next navigation (mirrors PageSlider).
const PagePreview = ({ journalId, tftUrl }) => {
  const [state, setState] = useState(null);
  const [spreadIndex, setSpreadIndex] = useState(0);
  const { width } = useWindowSize();

  const { data, error } = useSWR(bookPagesKey(journalId), getSWR, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
    refreshInterval: POLLING_STATES.has(state) ? 3000 : 0,
  });

  useEffect(() => {
    if (data?.status === 200) setState(getGenerationState(data));
  }, [data]);

  const pages = useMemo(() => {
    try {
      return data?.data?.data ? transformAllPages(data.data) : [];
    } catch (e) {
      return [];
    }
  }, [data]);

  const totalSpreads = Math.ceil(pages.length / 2);
  useEffect(() => {
    if (spreadIndex > Math.max(0, totalSpreads - 1)) setSpreadIndex(0);
  }, [totalSpreads, spreadIndex]);

  const isBuilding = !error && (!data || POLLING_STATES.has(state));

  if (isBuilding) {
    return (
      <Flex flexDirection="column" alignItems="center" py={5}>
        <Loading local />
        <Text fontFamily="Poppins" fontSize="14px" color={FT_COLORS.muted} mt={3}>
          Adding family members…
        </Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex flexDirection="column" alignItems="center" py={5}>
        <Text fontFamily="Poppins" fontSize="14px" color={FT_COLORS.muted}>
          We couldn&apos;t load the preview right now.
        </Text>
      </Flex>
    );
  }

  const isDesktop = width >= 768;
  const spreadWidth = Math.min(isDesktop ? 620 : width - 40, 620);
  const pageWidth = Math.floor(spreadWidth / 2);

  const firstAttrs = pages[0]?.attributes || {};
  const nativeW = Number(firstAttrs.width) || pageWidth;
  const nativeH = Number(firstAttrs.height) || pageWidth * 1.3;
  const pageHeight = Math.round(nativeH * (pageWidth / nativeW));

  const hasNav = totalSpreads > 1;

  return (
    <Box pb={7}>
      <Heading fontFamily="Bitter" fontWeight={500} textAlign="center" fontSize="30px" color={FT_COLORS.text}>
        Talking Family Tree page preview
      </Heading>
      <Text fontFamily="Poppins" fontSize="16px" color="#64748B" lineHeight="20px" textAlign="center" mt={1} mb={3}>
        Watch these pages fill in as more family members join
      </Text>

      {pages.length === 0 ? (
        <Text fontFamily="Poppins" fontSize="14px" color={FT_COLORS.muted} textAlign="center" py={4}>
          No pages yet.
        </Text>
      ) : (
        <Flex alignItems="center" justifyContent="center" sx={{ gap: '8px' }}>
          {hasNav && (
            <Arrow dir="left" disabled={spreadIndex === 0} onClick={() => setSpreadIndex((i) => Math.max(0, i - 1))} />
          )}

          <Box sx={{ position: 'relative', width: `${spreadWidth}px`, maxWidth: '100%', overflow: 'hidden', boxShadow: 'container', borderRadius: '8px' }}>
            <Flex
              css="flex-wrap: nowrap;"
              sx={{ transform: `translateX(-${spreadIndex * 100}%)`, transition: 'transform 0.4s ease' }}
            >
              {Array.from({ length: totalSpreads }).map((_, s) => {
                const left = pages[s * 2];
                const right = pages[s * 2 + 1];
                return (
                  <Flex key={left.id} css="flex: 0 0 100%;" justifyContent="center">
                    <PageCard page={left} index={s * 2} targetWidth={pageWidth} />
                    {right
                      ? <PageCard page={right} index={s * 2 + 1} targetWidth={pageWidth} />
                      : <BlankPage w={pageWidth} h={pageHeight} />}
                  </Flex>
                );
              })}
            </Flex>

            {/* Soft shadow between the two pages (book gutter). */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '50%',
                width: '14px',
                transform: 'translateX(-50%)',
                pointerEvents: 'none',
                zIndex: 2,
                background:
                  'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.28) 50%, rgba(0,0,0,0) 100%)',
              }}
            />
          </Box>

          {hasNav && (
            <Arrow
              dir="right"
              disabled={spreadIndex >= totalSpreads - 1}
              onClick={() => setSpreadIndex((i) => Math.min(totalSpreads - 1, i + 1))}
            />
          )}
        </Flex>
      )}

      {hasNav && (
        <Text fontFamily="Poppins" fontSize="13px" color={FT_COLORS.muted} textAlign="center" mt={2}>
          {`Pages ${spreadIndex * 2 + 1}–${Math.min(spreadIndex * 2 + 2, pages.length)} of ${pages.length}`}
        </Text>
      )}

      <Text fontFamily="Bitter" fontWeight={500} color={FT_COLORS.text} fontSize="30px" textAlign="center" mt={4} mb={2}>
        Your family&apos;s videos
      </Text>
      <Text fontFamily="Poppins" fontSize="16px" color="#64748B" lineHeight="20px" textAlign="center" mt={1} mb={3}>
        See what will happen when you open the videos from your printed book.
      </Text>
      <Button m="auto" fontFamily="Poppins" fontWeight={500} fontSize="16px" color="white" textAlign="center"
        onClick={() => window.open(tftUrl, '_blank', 'noopener,noreferrer')}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1
        }}
      >

        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M2.5 9.16667H9.16667V2.5H2.5V9.16667ZM4.16667 4.16667H7.5V7.5H4.16667V4.16667ZM2.5 17.5H9.16667V10.8333H2.5V17.5ZM4.16667 12.5H7.5V15.8333H4.16667V12.5ZM10.8333 2.5V9.16667H17.5V2.5H10.8333ZM15.8333 7.5H12.5V4.16667H15.8333V7.5ZM10.8417 10.8333H12.5083V12.5H10.8417V10.8333ZM12.5083 12.5H14.175V14.1667H12.5083V12.5ZM10.8417 14.1667H12.5083V15.8333H10.8417V14.1667ZM14.175 14.1667H15.8417V15.8333H14.175V14.1667ZM15.8417 15.8333H17.5083V17.5H15.8417V15.8333ZM12.5083 15.8333H14.175V17.5H12.5083V15.8333ZM14.175 10.8333H15.8417V12.5H14.175V10.8333ZM15.8417 12.5H17.5083V14.1667H15.8417V12.5Z" fill="white" />
        </svg>
        Watch Videos
      </Button>
    </Box>
  );
};

PagePreview.propTypes = {
  journalId: PropTypes.string.isRequired,
  tftUrl: PropTypes.string.isRequired,
};

export default PagePreview;
